import { useMemo, useState } from 'react';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useSpace } from '../../../../hooks/useSpace';
import { useGetInboxEmails } from '../../api/useGetInboxEmails';
import { useGetInboxEmailsCount } from '../../api/useGetInboxEmailsCount';
import type { InboxEmail, InboxEmailStatus } from '../../types/inbox.types';

type Selection = {
  email: InboxEmail;
  /** Search the email was opened under: a later search may exclude it from the results. */
  search: string;
};
import { flattenUniquePages, getId } from '../../../../utils/pagination';

export function useInboxPage() {
  const { isAdmin } = usePermissions();
  const { currentSpace } = useSpace();
  const [currentTab, setCurrentTab] = useState<InboxEmailStatus>('pending');
  const [selection, setSelection] = useState<Selection | null>(null);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm);

  const {
    data,
    isLoading: isLoadingEmails,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  } = useGetInboxEmails({
    spaceId: currentSpace?.id,
    status: currentTab,
    search: debouncedSearch,
  });

  const { data: pendingCount = 0 } = useGetInboxEmailsCount({
    spaceId: currentSpace?.id,
    status: 'pending',
  });

  const emails = useMemo(() => flattenUniquePages(data?.pages, getId), [data]);

  const { data: emailsCount = 0 } = useGetInboxEmailsCount({
    spaceId: currentSpace?.id,
    status: currentTab,
    search: debouncedSearch,
  });

  // Fresh row from the list when loaded. Otherwise keep the opened email while only the search
  // changed, so typing a search doesn't close the modal; a status change still drops it.
  const selectedEmail = selection
    ? emails.find((e) => e.id === selection.email.id)
      ?? (selection.search !== debouncedSearch ? selection.email : null)
    : null;

  function handleTabChange(tab: InboxEmailStatus) {
    setCurrentTab(tab);
    setSelection(null);
  }

  function handleLoadMore() {
    fetchNextPage();
  }

  // Resolves with every loaded email, so the detail modal can step past the last loaded one.
  async function handleLoadMoreEmails() {
    const result = await fetchNextPage();
    return flattenUniquePages(result.data?.pages, getId);
  }

  function handleSelectEmail(id: string) {
    const email = emails.find((e) => e.id === id);
    if (email) {
      handleOpenEmail(email);
    }
  }

  function handleOpenEmail(email: InboxEmail) {
    setSelection({ email, search: debouncedSearch });
  }

  function handleCloseDetailModal() {
    setSelection(null);
  }

  function handleOpenConfigModal() {
    setIsConfigModalOpen(true);
  }

  function handleCloseConfigModal() {
    setIsConfigModalOpen(false);
  }

  return {
    isAdmin,
    currentSpace,
    currentTab,
    selectedEmailId: selection?.email.id ?? null,
    selectedEmail,
    emails,
    pendingCount,
    isLoadingEmails,
    searchTerm,
    setSearchTerm,
    hasMoreEmails: Boolean(hasNextPage),
    emailsCount,
    isFetchingNextPage,
    isLoadMoreError: isFetchNextPageError,
    handleLoadMore,
    handleLoadMoreEmails,
    handleOpenEmail,
    isConfigModalOpen,
    handleTabChange,
    handleSelectEmail,
    handleCloseDetailModal,
    handleOpenConfigModal,
    handleCloseConfigModal,
  };
}
